/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { useContext } from 'react';
import { Redirect, useMatch } from '@reach/router';
import Tabs from '@atlaskit/tabs';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import { useTabs } from './utils/use-tabs';
import CodeDocs from '../../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/content/code-docs';
import ContentRenderer from '../../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/content/content-renderer';
import { Tab, TabPanel, TabList } from '../../components/tabs';
import { PageContext } from '../../components/page-context';

interface TabbedSectionProps {
  data: any;
  pageContext: { hasCodeDocs: boolean };
}

// Pull the tabs up so the TabList sits on the Hero section
const tabsContainerStyles = css({
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  marginTop: '-4rem',
});

const TabbedSection = ({ data, pageContext }: TabbedSectionProps) => {
  const { hasCodeDocs } = pageContext;

  const currentPage = useContext(PageContext);
  const { createAnalyticsEvent } = useAnalyticsEvents();

  const tabs = useTabs(data.mdx.nodes, currentPage.uri, hasCodeDocs);
  const match = useMatch(`${currentPage.uri}/:tab`);
  const initialTab = tabs.findIndex((t) => t.name === match?.tab);

  if (initialTab < 0) {
    return <Redirect to={tabs[0].to} noThrow />;
  }

  const fireTabAnalytics = (currentTab: string, nextTab: string) => () => {
    const analyticsEvent = createAnalyticsEvent({
      actionSubject: 'tab',
      action: 'clicked',
    });

    fireUIAnalytics(analyticsEvent, 'nextTab', {
      packageName: data.workspaceInfo.name,
      currentTab,
      nextTab,
    });
  };

  const getTabContent = (tab: {
    to?: string;
    name: any;
    order?: any;
    content: any;
  }) => {
    if (tab.name === 'code' && hasCodeDocs) {
      return (
        <CodeDocs workspaceInfo={data.workspaceInfo} propsMdx={data.props} />
      );
    }
    if (tab.name === 'changelog') {
      return (
        <ContentRenderer
          id={data.changelog.mdx.id}
          body={data.changelog.mdx.body}
        />
      );
    }

    return <ContentRenderer id={tab.content.id} body={tab.content.body} />;
  };

  return (
    <div css={tabsContainerStyles}>
      <Tabs
        id="package-page"
        defaultSelected={initialTab}
        shouldUnmountTabPanelOnChange
      >
        <TabList>
          {tabs.map((tab) => (
            <Tab
              key={tab.name}
              name={tab.name}
              to={tab.to}
              onClick={fireTabAnalytics(tab.name, tab.to)}
            />
          ))}
        </TabList>
        {tabs.map((tab) => (
          <TabPanel key={tab.name}>{getTabContent(tab)}</TabPanel>
        ))}
      </Tabs>
    </div>
  );
};

export default TabbedSection;
